import React, { useId } from 'react';
import { MockupTag } from './MockupTag';
export type SectionBg = 'white' | 'cream' | 'grey';
interface SectionProps {
  id?: string;
  bg?: SectionBg;
  title: string;
  description?: string;
  subhead?: string;
  mockup?: boolean | string;
  aside?: React.ReactNode;
  children?: React.ReactNode;
}
const panelClass: Record<Exclude<SectionBg, 'white'>, string> = {
  cream: 'bg-cream',
  grey: 'bg-mist'
};

// Coloured sections render as rounded, inset panels so the page reads as a stack of soft surfaces.
export function Section({
  id,
  bg = 'white',
  title,
  description,
  subhead,
  mockup,
  aside,
  children
}: SectionProps) {
  const headingId = useId();
  const [lead, accent] = splitTitle(title);
  const inner = <div className="mx-auto max-w-content px-5 py-16 md:px-8 md:py-24" data-id="element-839" data-mp="3aujqobjdvwvm">
      <div data-id="element-840" data-mp="1kgud95o0te82">
        {mockup ? <MockupTag note={typeof mockup === 'string' ? mockup : undefined} className="mb-4" data-id="element-841" data-mp="1ki4cvzaiuugt" /> : null}
        <h2 id={headingId} data-id="element-842" data-mp="1ghlh5u5id49c">
          {lead ? <span className="block type-h2-light text-ink" data-id="element-843" data-mp="24l9rnicuw81k">{lead}</span> : null}
          <span className="block type-h2 text-brand" data-id="element-844" data-mp="24n2wqt7tmk7g">{accent}</span>
        </h2>
        {subhead || description || aside ? <div className="mt-6 md:mt-8" data-id="element-845" data-mp="2ejftjzynxe6f">
            {subhead ? <p className="type-body font-semibold text-ink" data-id="element-846" data-mp="2ehuxu70m3p6z">{subhead}</p> : null}
            {description ? <p className={`${subhead ? 'mt-3' : ''} type-body text-ink`} data-id="element-847" data-mp="2jaxqmcxkeum4">{description}</p> : null}
            {aside ? <div className={subhead || description ? 'mt-5' : ''} data-id="element-848" data-mp="2o8pi55uaseps">{aside}</div> : null}
          </div> : null}
      </div>
      {children ? <div className="mt-10 md:mt-14" data-id="element-849" data-mp="2x9xf9kl2v9ia">{children}</div> : null}
    </div>;
  if (bg === 'white') {
    return <section id={id} aria-labelledby={headingId} className="w-full bg-white" data-id="element-850" data-mp="329jtnxcn6vso">
        {inner}
      </section>;
  }
  return <section id={id} aria-labelledby={headingId} className="w-full bg-white px-2 py-2 md:px-4" data-id="element-851" data-mp="12x7kl0mvxw9u">
      <div className={`rounded-[28px] md:rounded-[40px] ${panelClass[bg]}`} data-id="element-852" data-mp="17vse6h7mthpb">{inner}</div>
    </section>;
}

// Two-line title: the first half of the words sits on a light line, the rest is the bold orange accent.
function splitTitle(title: string): [string, string] {
  const words = title.trim().split(/\s+/);
  if (words.length < 2) return ['', title];
  const cut = Math.floor(words.length / 2);
  return [words.slice(0, cut).join(' '), words.slice(cut).join(' ')];
}